import { FuseNavigationItem } from '@fuse/components/navigation';
import {
    hasAnyRole,
    ORG_ADMIN_AREA_ROLES,
    ORG_OWNER_TIER_ROLES,
    PLATFORM_STAFF_ROLES,
} from 'app/shared/constants';
import { basic, group } from './fallback-navigation';

/**
 * photoqa-central fallback — mirrors BE seed `PhotoFlowMenuSeedData.PhotoQaCentralResource`,
 * with one deliberate correction: the seed's "Gallery" (/photoqa/gallery)
 * item is dropped here because that route no longer exists (it was a
 * redirectTo '/albums' stub, deleted outright by PFL-2258 after the
 * PFL-1685 B2 dedup left it with nothing of its own to show) — the seed
 * itself is stale on this one item; the "Albums" entry above already
 * covers it.
 *
 * The photoqa + photoqa-account groups are gated `NeraRoles.Admin` (the FE /photoqa route is
 * admin-only). The photoqa-admin group (privacy, export presets, scoring configs) is
 * platform-config managed centrally by the global org (PFL-2110): it is shown ONLY when
 * `isGlobalOrgAdmin` — a SUPER_ADMIN, or an ADMIN of the system/global org — matching the
 * backend `RequireGlobalOrgAdmin` policy and the `/photoqa/admin/*` route guard. A customer-org
 * ADMIN sees the operational PhotoQA groups but not the admin-config group.
 */
export function buildPhotoQaCentralFallbackNav(
    userRoles: string | string[] | undefined,
    isGlobalOrgAdmin: boolean
): FuseNavigationItem[] {
    if (
        !hasAnyRole(userRoles, [
            ...ORG_ADMIN_AREA_ROLES,
            ...PLATFORM_STAFF_ROLES,
        ])
    ) {
        return [];
    }

    const items: FuseNavigationItem[] = [
        group('photoqa', 'nav.fallback.photoqa', [
            basic(
                'photoqa-dashboard',
                'nav.fallback.photoqaDashboard',
                '/photoqa/dashboard',
                'heroicons_outline:home'
            ),
            basic(
                'photoqa-analyze',
                'nav.fallback.analyze',
                '/photoqa/analyze',
                'heroicons_outline:sparkles'
            ),
            basic(
                'photoqa-history',
                'nav.fallback.history',
                '/photoqa/history',
                'heroicons_outline:clock'
            ),
        ]),
        group('photoqa-account', 'nav.fallback.photoqaAccount', [
            // WS-H (PFL-2569 MR9): studio owner-signed consent screen —
            // `/photoqa/consents` (RoleGuard(ORG_OWNERS); moved out of the
            // platform-admin namespace by kb#2706). Owner-tier only, and never
            // for a global-org admin: in the SystemOrg context consent is the
            // personal `/me/privacy` page (already in the account group), not
            // a studio commitment.
            ...(hasAnyRole(userRoles, [...ORG_OWNER_TIER_ROLES]) &&
            !isGlobalOrgAdmin
                ? [
                      basic(
                          'photoqa-consents',
                          'nav.fallback.studioConsents',
                          '/photoqa/consents',
                          'heroicons_outline:shield-check'
                      ),
                  ]
                : []),
            basic(
                'photoqa-webhooks',
                'nav.fallback.webhooks',
                '/photoqa/webhooks',
                'heroicons_outline:bolt'
            ),
        ]),
    ];

    // PFL-2110: the admin-config group is platform-managed — shown to a global-org admin
    // (or super-admin) only, never a customer-org admin.
    if (isGlobalOrgAdmin) {
        items.push(
            group('photoqa-admin', 'nav.fallback.photoqaAdmin', [
                basic(
                    'photoqa-privacy',
                    'nav.fallback.privacySettings',
                    '/photoqa/admin/privacy',
                    'heroicons_outline:lock-closed'
                ),
            ])
        );
    }

    return items;
}
